import ViewerContainer from 'app/containers/ViewerContainer/ViewerContainer';
import { PDF_DOCUMENT_OPTIONS } from 'core/modules/components/pdfViewers/pdfDocumentOptions';
import { CONTENT_TYPES } from 'core/variables/constant';
import PropTypes from 'prop-types';
import React from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import PdfContentNavigation from './PdfContentNavigation';
import usePdfContent from './usePdfContent';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

function PdfContent({
  lang,
  url,
  onDocumentLoadSuccess = () => {},
  pageNumber = null,
  contentGuid = null,
  pagesRange = null,
  onClickFullScreen = null,
  variant = 'full-width',
  contentType,
}) {
  const {
    containerRef,
    containerSize,
    currentPageForDisplay,
    goToNextPage,
    goToPreviousPage,
    hasError,
    isNextDisabled,
    isPreviousDisabled,
    onLoadError,
    onLoadSuccess,
    pageRegionLabel,
    pagesToRender,
    showNavigation,
    totalPagesForDisplay,
    urlResource,
  } = usePdfContent({ contentGuid, onDocumentLoadSuccess, pageNumber, pagesRange, url, variant, contentType });

  function handleFullScreen() {
    window.open(`/viewerfull/${contentGuid}`, '_blank');
  }

  const effectiveOnClickFullScreen = onClickFullScreen || (contentGuid ? handleFullScreen : null);

  return (
    <div className={`pdf-content ${variant ? `pdf-content--${variant}` : ''}`}>
      {showNavigation && (
        <PdfContentNavigation
          currentPage={currentPageForDisplay}
          disabled={hasError}
          isNextDisabled={isNextDisabled}
          isPreviousDisabled={isPreviousDisabled}
          onClickNext={goToNextPage}
          onClickFullScreen={effectiveOnClickFullScreen}
          onClickPrevious={goToPreviousPage}
          totalPages={totalPagesForDisplay}
        />
      )}

      {contentType !== CONTENT_TYPES.OFFICE && (
        // Región con nombre para saltar al texto con el rotor del lector (TAN-12086). El `lang` va en el
        // contenedor interior: en la región, el nombre (idioma de la interfaz) se leería con la voz del contenido.
        <section aria-label={pageRegionLabel} className="pdf-content__region">
          <div className="pdf-content__container" lang={lang} ref={containerRef}>
            {urlResource && containerSize > 0 && (
              <Document file={{ url: urlResource }} onLoadError={onLoadError} onLoadSuccess={onLoadSuccess} options={PDF_DOCUMENT_OPTIONS}>
                {pagesToRender.map((pageNum, index) => (
                  <Page
                    key={`page-${pageNum}-${index}`}
                    pageNumber={pageNum}
                    renderAnnotationLayer={false}
                    {...(variant === 'full-height' ? { height: containerSize } : { width: containerSize })}
                  />
                ))}
              </Document>
            )}
          </div>
        </section>
      )}

      {contentType === CONTENT_TYPES.OFFICE && (
        <div className="office-content__container" ref={containerRef}>
          <ViewerContainer
            config={{ pdfConfig: [], disabledFetchData: true }}
            contentGuid={contentGuid}
            showEmbed
            standalone={true}
            shouldEnableFullWidth={true}
            shouldConsumption={false}
          />
        </div>
      )}
    </div>
  );
}

PdfContent.propTypes = {
  contentGuid: PropTypes.string,
  lang: PropTypes.string,
  onClickFullScreen: PropTypes.func,
  onDocumentLoadSuccess: PropTypes.func,
  pageNumber: PropTypes.number,
  pagesRange: PropTypes.array,
  url: PropTypes.string,
  variant: PropTypes.oneOf(['full-width', 'full-height']),
  contentType: PropTypes.string,
};

export default PdfContent;
